import { MediaManagerTemplate } from 'modules/core/domain/interfaces/supabase/media-manager/entity';
import { TEMPLATE_FILE_FORMAT, TEMPLATE_FILE_SUFFIX, TEMPLATE_FILE_VERSION, TemplateFile, TemplateFileEntry } from './template-file';

export type TemplateFileSource = Pick<
  MediaManagerTemplate,
  | 'name'
  | 'objects'
  | 'version'
  | 'width'
  | 'height'
  | 'sportType'
  | 'templateType'
  | 'mediaKind'
  | 'templateSubtypes'
  | 'matchLayout'
  | 'playerRanking'
>;

interface BuildTemplateFileOptions {
  origin: string;
  exportedAt: string;
}

export function toTemplateFileEntry(template: TemplateFileSource): TemplateFileEntry {
  return {
    name: template.name,
    version: template.version,
    width: template.width,
    height: template.height,
    sportType: template.sportType,
    templateType: template.templateType,
    mediaKind: template.mediaKind,
    templateSubtypes: [...template.templateSubtypes],
    matchLayout: template.matchLayout,
    playerRanking: template.playerRanking,
    objects: template.objects,
  };
}

export function buildTemplateFile(
  templates: readonly TemplateFileSource[],
  { origin, exportedAt }: BuildTemplateFileOptions,
): TemplateFile {
  return {
    format: TEMPLATE_FILE_FORMAT,
    formatVersion: TEMPLATE_FILE_VERSION,
    exportedAt,
    source: { origin },
    templates: templates.map(toTemplateFileEntry),
  };
}

// Characters Windows (the strictest of the targets) refuses in a file name,
// plus control characters.
// eslint-disable-next-line no-control-regex
const FORBIDDEN_FILE_NAME_CHARS = /[\\/:*?"<>| -]/g;

export function sanitizeFileName(value: string): string {
  const cleaned = value.replace(FORBIDDEN_FILE_NAME_CHARS, '_').replace(/\s+/g, ' ').trim();
  // A trailing dot or space is silently dropped by Windows — do not leave one.
  return cleaned.replace(/[. ]+$/, '') || 'template';
}

/**
 * `<name>.imgame-template.json` for a single template;
 * `<label>-templates-<YYYY-MM-DD>.imgame-template.json` for a batch.
 */
export function templateFileName(
  templates: readonly Pick<MediaManagerTemplate, 'name'>[],
  label = 'media-manager',
  date: Date = new Date(),
): string {
  if (templates.length === 1) {
    return `${sanitizeFileName(templates[0].name)}${TEMPLATE_FILE_SUFFIX}`;
  }
  const day = date.toISOString().slice(0, 10);
  return `${sanitizeFileName(label)}-templates-${day}${TEMPLATE_FILE_SUFFIX}`;
}
